import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Share2, Copy, Check, ArrowRight, Sparkles, Brain, BookOpen, Heart } from 'lucide-react';
import { LogoFull, LogoSmall } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useMultilingualSEO } from '@/hooks/useMultilingualSEO';

const API = '';
const SITE_URL = 'https://livinggeeta.com';

const LANG_LABELS = {
  ta: { label: 'தமிழ் (Tamil)', font: "font-['Noto_Sans_Tamil']" },
  te: { label: 'తెలుగు (Telugu)', font: "font-['Noto_Sans_Telugu']" },
  bn: { label: 'বাংলা (Bengali)', font: "font-['Noto_Sans_Bengali']" },
  kn: { label: 'ಕನ್ನಡ (Kannada)', font: "font-['Noto_Sans_Kannada']" },
  or: { label: 'ଓଡ଼ିଆ (Odia)', font: "font-['Noto_Sans_Oriya']" },
  as: { label: 'অসমীয়া (Assamese)', font: "font-['Noto_Sans_Bengali']" },
  gu: { label: 'ગુજરાતી (Gujarati)', font: "font-['Noto_Sans_Gujarati']" },
  ml: { label: 'മലയാളം (Malayalam)', font: "font-['Noto_Sans_Malayalam']" },
  es: { label: 'Español (Spanish)', font: "" },
};

function ShareBar({ verse, chapter, verseNum }) {
  const [copied, setCopied] = useState(false);
  const url = `${SITE_URL}/verse/${chapter}/${verseNum}`;
  const firstLine = verse.sanskrit?.split('\n')[0] || '';
  const shareText = `${firstLine}\n— Bhagavad Gita ${chapter}.${verseNum}\n\nRead full meaning: ${url}`;

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="share-bar">
      <span className="text-xs text-muted-foreground mr-1">Share</span>
      <button onClick={shareWhatsApp} className="p-2 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors" title="Share on WhatsApp" data-testid="share-whatsapp">
        <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </button>
      <button onClick={shareTwitter} className="p-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 transition-colors" title="Share on X/Twitter" data-testid="share-twitter">
        <svg className="w-4 h-4 text-foreground/70" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </button>
      <button onClick={shareFacebook} className="p-2 rounded-lg bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition-colors" title="Share on Facebook" data-testid="share-facebook">
        <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </button>
      <button onClick={copyLink} className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors" title="Copy link" data-testid="share-copy">
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
      </button>
    </div>
  );
}

export default function VersePage() {
  const { chapter, verse: verseNum, lang: urlLang } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getCanonicalUrl, generateHreflangTags, currentLangConfig, generateBreadcrumbSD, SITE_URL: seoSiteUrl } = useMultilingualSEO('verse');

  // If URL has a language prefix, sync i18n to it
  useEffect(() => {
    if (urlLang && ['ta', 'te', 'bn', 'kn', 'or', 'as', 'gu', 'ml', 'es', 'en', 'hi'].includes(urlLang) && i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [urlLang, i18n]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/public/verse/${chapter}/${verseNum}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setVerse(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [chapter, verseNum]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!verse) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Verse not found</h1>
          <Link to="/chapters" className="text-primary hover:underline">Browse all chapters</Link>
        </div>
      </div>
    );
  }

  const langPrefix = urlLang ? `/${urlLang}` : '';
  const translitFirstWords = verse.transliteration?.split(' ').slice(0, 4).join(' ') || '';
  const canonicalUrl = getCanonicalUrl(`/verse/${chapter}/${verseNum}`);

  return (
    <div className="min-h-screen bg-background" data-testid="verse-page">
      <Helmet>
        <title>{`${t('common.bhagavad_gita')} ${chapter}.${verseNum} — ${translitFirstWords} | Meaning | Living Geeta`}</title>
        <meta name="description" content={t('seo.verse_desc', { 
          chapter, 
          verse: verseNum, 
          translation: verse.translation_en?.substring(0, 150) || '' 
        })} />
        <link rel="canonical" href={canonicalUrl} />
        {generateHreflangTags(`/verse/${chapter}/${verseNum}`)}
        
        <meta property="og:title" content={`Bhagavad Gita ${chapter}.${verseNum} — ${translitFirstWords} | Meaning in Hindi & English`} />
        <meta property="og:description" content={verse.translation_en?.substring(0, 150)} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={currentLangConfig.locale} />
        <meta property="og:image" content={`${SITE_URL}/og-logo.png`} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Bhagavad Gita ${chapter}.${verseNum} — ${translitFirstWords} | Meaning`} />
        <meta name="twitter:description" content={verse.translation_en?.substring(0, 150)} />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `Bhagavad Gita Chapter ${chapter}, Verse ${verseNum} — Meaning & Commentary`,
          "description": verse.translation_en,
          "url": canonicalUrl,
          "inLanguage": currentLangConfig.hreflang,
          "keywords": "Bhagavad Gita quotes, Bhagavad Gita slokas, Bhagavad Gita meaning, Gita saar, Krishna teachings",
          "publisher": { "@type": "Organization", "name": "Living Geeta", "url": SITE_URL },
          "isPartOf": { "@type": "Book", "name": "Bhagavad Gita", "alternateName": ["Bhagwad Geeta", "Bhagwat Gita", "Srimad Bhagavad Gita", "भगवद गीता", "श्रीमद भगवद गीता"] }
        })}</script>
        <script type="application/ld+json">{JSON.stringify(
          generateBreadcrumbSD([
            { name: "Bhagavad Gita", url: `${SITE_URL}/chapters` },
            { name: `Chapter ${chapter}`, url: `${SITE_URL}/chapter/${chapter}` },
            { name: `Verse ${verseNum}` }
          ])
        )}</script>
      </Helmet>

      {/* Nav */}
      <PublicNav />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
        {/* Breadcrumb + Nav */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/chapters" className="hover:text-foreground transition-colors">Bhagavad Gita</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/chapter/${chapter}`} className="hover:text-foreground transition-colors">Chapter {chapter}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">Verse {verseNum}</span>
          </div>
          <div className="flex items-center gap-3">
            {verse.prev_verse && (
              <button onClick={() => navigate(`${langPrefix}/verse/${verse.prev_verse.chapter}/${verse.prev_verse.verse}`)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="prev-verse-btn">
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
            )}
            {verse.next_verse && (
              <button onClick={() => navigate(`${langPrefix}/verse/${verse.next_verse.chapter}/${verse.next_verse.verse}`)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="next-verse-btn">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Verse Reference */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-primary/60 mb-2 font-medium">
            Chapter {chapter} &middot; {verse.chapter_name_en}
          </p>
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            {t('common.bhagavad_gita')} {t('common.chapter')} {chapter}, {t('common.verse')} {verseNum}
          </h1>
          {verse.chapter_name_hi && (
            <p className="font-['Noto_Sans_Devanagari'] text-sm text-muted-foreground mt-1">{verse.chapter_name_hi}</p>
          )}
        </div>

        {/* Sanskrit Shloka */}
        <div className="relative bg-gradient-to-r from-amber-50/80 to-orange-50/60 border border-amber-200/40 rounded-2xl p-6 sm:p-8 mb-8" data-testid="verse-sanskrit-section">
          {/* Subtle Om watermark */}
          <svg className="absolute top-4 right-4 w-16 h-16 text-amber-300/30 pointer-events-none" viewBox="0 0 100 100" aria-hidden="true">
            <text x="50" y="70" textAnchor="middle" fontSize="60" fill="currentColor" fontFamily="serif">&#x0950;</text>
          </svg>
          <p className="text-xs uppercase tracking-[0.15em] text-amber-700/70 mb-4 font-medium text-center">॥ श्लोक ॥</p>
          <p className="font-['Noto_Sans_Devanagari'] text-xl sm:text-2xl leading-[2.2] text-amber-900/90 whitespace-pre-line relative z-10 text-center italic" data-testid="verse-sanskrit-text">
            {verse.sanskrit}
          </p>
        </div>

        {/* Transliteration - Only show for English */}
        {(i18n.language === 'en' || i18n.language?.startsWith('en')) && verse.transliteration && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/60 mb-2 font-medium">Transliteration</p>
            <p className="text-base text-muted-foreground italic leading-relaxed" data-testid="verse-transliteration">
              {verse.transliteration}
            </p>
          </div>
        )}

        {/* Meanings / Commentary - Show only selected language */}
        <div className="mb-8">
          {/* Regional languages (Tamil, Telugu, Bengali, Kannada) */}
          {['ta', 'te', 'bn', 'kn', 'or', 'as', 'gu', 'ml', 'es'].includes(i18n.language) && verse[`meaning_${i18n.language}`] && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/60 mb-2 font-medium">
                {t('verse.commentary')} ({LANG_LABELS[i18n.language]?.label || i18n.language})
              </p>
              <p className={`text-foreground leading-relaxed ${LANG_LABELS[i18n.language]?.font || ''}`}
                 data-testid={`verse-meaning-${i18n.language}`}>
                {verse[`meaning_${i18n.language}`]}
              </p>
            </div>
          )}
          
          {/* English - Show translation and detailed English commentary */}
          {(i18n.language === 'en' || i18n.language?.startsWith('en')) && (
            <div className="space-y-6">
              {verse.translation_en && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/60 mb-2 font-medium">English Translation</p>
                  <p className="text-foreground leading-relaxed" data-testid="verse-translation-en">{verse.translation_en}</p>
                </div>
              )}
              {verse.meaning_en && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/60 mb-2 font-medium">Detailed Commentary</p>
                  <p className="text-foreground/90 leading-[1.9]" data-testid="verse-meaning-en">{verse.meaning_en}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Hindi - Show only detailed Hindi commentary (no translation) */}
          {i18n.language === 'hi' && (
            <div className="space-y-6">
              {verse.meaning_hi && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/60 mb-2 font-medium">{t('verse.commentary', { defaultValue: 'विस्तृत व्याख्या' })}</p>
                  <p className="font-['Noto_Sans_Devanagari'] text-foreground/90 leading-[1.9]" data-testid="verse-meaning-hi">{verse.meaning_hi}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Share Bar */}
        <div className="flex items-center justify-between py-5 border-y border-border/30 mb-10">
          <ShareBar verse={verse} chapter={chapter} verseNum={verseNum} />
          <div className="flex items-center gap-3">
            {verse.prev_verse && (
              <Link to={`${langPrefix}/verse/${verse.prev_verse.chapter}/${verse.prev_verse.verse}`}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </Link>
            )}
            {verse.next_verse && (
              <Link to={`${langPrefix}/verse/${verse.next_verse.chapter}/${verse.next_verse.verse}`}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-primary/[0.04] to-secondary/[0.04] border border-border/30 rounded-2xl p-6 sm:p-10" data-testid="verse-cta-section">
          <h2 className="font-['Playfair_Display'] text-xl sm:text-2xl font-semibold text-foreground mb-3">
            Go Beyond Reading — Live the Gita
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl">
            Living Geeta transforms the Bhagavad Gita from ancient text into a living daily practice. Here's what awaits you:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Brain, title: "Guidance by Krishna", desc: "Ask any life question and receive Gita-backed guidance with relevant shlokas" },
              { icon: Heart, title: "Guna Assessment", desc: "Discover your Sattva-Rajas-Tamas nature through a personalized quiz" },
              { icon: BookOpen, title: "Daily Verse", desc: "A personally matched verse each day to guide your transformation" },
              { icon: Sparkles, title: "21-Day Programs", desc: "Structured journeys for anger, anxiety, purpose — backed by Krishna's teachings" },
            ].map(f => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <f.icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{f.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/signup" className="bg-primary text-primary-foreground rounded-full px-7 py-3 text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all hover:-translate-y-0.5 inline-flex items-center gap-2" data-testid="verse-cta-signup">
              Start Your Free Journey <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-xs text-muted-foreground">Free forever. A non-profit initiative.</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
